/* Agendamientos - reservar (o reprogramar) un turno (TD-11766).
 *
 * Flujo: día -> turno -> preguntas del formulario -> confirmar. El backend
 * vuelve a validar todo (turno vigente, cupo, límites por persona, choque con
 * otra cita) con un lock por agenda, así que la pantalla solo guía.
 * Con ?reprogramar=<id> cancela la cita original y toma la nueva en una sola
 * operación, conservando las respuestas del formulario.
 */
import { IonButton, IonContent, IonIcon, IonPage, useIonAlert, useIonToast } from "@ionic/react";
import { CircularProgress, TextField } from "@material-ui/core";
import { arrowBack, calendarOutline, locationOutline, peopleOutline, timeOutline } from "ionicons/icons";
import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router-dom";
import Header from "src/components/Header";
import AgendaAPI, { avisarCambioAgenda, TemaPublico, Turno } from "src/services/agenda";
import {
  agruparPorDia,
  CATEGORIAS,
  colorCategoria,
  ETIQUETA_MODALIDAD,
  formatFechaLarga,
  hora,
} from "src/utils/agenda";
import { mensajeError } from "src/utils/errores";
import { formatFechaCorta } from "src/utils/fechas";
import "./Agenda.css";

/** 'YYYY-MM-DD' + 1 día. */
const diaSiguiente = (ymd: string) => {
  const d = new Date(`${ymd}T00:00:00`);
  d.setDate(d.getDate() + 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

const AgendaReservar: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const temaId = Number(id);
  const location = useLocation();
  const reprogramarId = Number(new URLSearchParams(location.search).get("reprogramar")) || null;
  const history = useHistory();
  const [present] = useIonToast();
  const [presentAlert] = useIonAlert();

  const [tema, setTema] = useState<TemaPublico | null>(null);
  const [turnos, setTurnos] = useState<Turno[]>([]);
  const [inscripcionAbierta, setInscripcionAbierta] = useState(true);
  // Los turnos llegan en tramos de ~2 meses hasta la fecha de fin de la agenda.
  const [cargadoHasta, setCargadoHasta] = useState<string | null>(null);
  const [hayMas, setHayMas] = useState(false);
  const [cargandoMas, setCargandoMas] = useState(false);
  const [cargando, setCargando] = useState(true);
  const [enviando, setEnviando] = useState(false);

  const [responsable, setResponsable] = useState<number | "TODOS">("TODOS");
  const [dia, setDia] = useState<string | null>(null);
  const [turno, setTurno] = useState<Turno | null>(null);
  const [respuestas, setRespuestas] = useState<Record<number, string>>({});

  const cargar = async () => {
    try {
      const [t, tr] = await Promise.all([AgendaAPI.tema(temaId), AgendaAPI.turnos(temaId)]);
      setTema(t);
      setTurnos(tr.datos);
      setCargadoHasta(tr.hasta);
      setHayMas(tr.hay_mas);
      setInscripcionAbierta(tr.inscripcion_abierta);
    } catch (err: any) {
      present({ message: mensajeError(err, "La agenda no está disponible"), duration: 3500, color: "danger" });
      history.replace("/agenda");
    } finally {
      setCargando(false);
    }
  };

  useEffect(() => {
    // La pantalla queda montada al pasar de una agenda a otra (por ej. desde un
    // push): se limpia la selección de la agenda anterior antes de cargar.
    setResponsable("TODOS");
    setDia(null);
    setTurno(null);
    setRespuestas({});
    setTurnos([]);
    setCargando(true);
    cargar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temaId]);

  const verMasFechas = async () => {
    if (!cargadoHasta) return;
    setCargandoMas(true);
    try {
      const tr = await AgendaAPI.turnos(temaId, diaSiguiente(cargadoHasta));
      setTurnos((prev) => [...prev, ...tr.datos]);
      setCargadoHasta(tr.hasta);
      setHayMas(tr.hay_mas);
    } catch (err: any) {
      present({ message: mensajeError(err, "No se pudieron cargar más fechas"), duration: 3500, color: "danger" });
    } finally {
      setCargandoMas(false);
    }
  };

  /** Vuelve a pedir los turnos desde el día (tras un turno que se ocupó), sin
   *  pasar de lo ya cargado: lo que sigue lo trae "Ver más fechas" y así no se
   *  duplican turnos. */
  const refrescarTramo = async (ymd: string) => {
    try {
      const tr = await AgendaAPI.turnos(temaId, ymd);
      const limite = [tr.hasta || ymd, cargadoHasta || ymd].sort()[0];
      const dia = (t: Turno) => t.inicio.slice(0, 10);
      setTurnos((prev) =>
        [...prev.filter((t) => dia(t) < ymd || dia(t) > limite), ...tr.datos.filter((t) => dia(t) <= limite)].sort((a, b) =>
          a.inicio.localeCompare(b.inicio)
        )
      );
    } catch {
      /* la grilla queda como estaba; el backend vuelve a validar al reservar */
    }
  };

  const filtrados = useMemo(
    () => turnos.filter((t) => responsable === "TODOS" || t.responsable_id === responsable),
    [turnos, responsable]
  );
  const dias = useMemo(() => agruparPorDia(filtrados, (t) => t.inicio), [filtrados]);

  // Día por defecto: el primero con lugar.
  useEffect(() => {
    if (!dias.length) {
      setDia(null);
      return;
    }
    if (!dia || !dias.some(([d]) => d === dia)) {
      const conLugar = dias.find(([, lista]) => lista.some((t) => t.disponibles > 0 && !t.mi_estado));
      setDia((conLugar || dias[0])[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dias]);

  const turnosDelDia = useMemo(() => dias.find(([d]) => d === dia)?.[1] || [], [dias, dia]);

  /** Recibe el turno explícito: el reintento desde el aviso de "turno completo"
   *  corre después de que la grilla se refrescó y se limpió la selección. */
  const enviar = async (elegido: Turno, listaEspera = false) => {
    if (!tema) return;
    // Al reprogramar se conservan las respuestas anteriores: el backend valida.
    if (!reprogramarId) {
      const falta = tema.preguntas.find((p) => p.obligatoria && !(respuestas[p.id] || "").trim());
      if (falta) {
        present({ message: `Respondé: ${falta.texto}`, duration: 3000, color: "warning" });
        return;
      }
    }
    setEnviando(true);
    try {
      const body = { inicio: elegido.inicio, responsable_id: elegido.responsable_id, respuestas };
      const res = reprogramarId
        ? await AgendaAPI.reprogramar(reprogramarId, body)
        : await AgendaAPI.reservar(temaId, { ...body, lista_espera: listaEspera });
      avisarCambioAgenda();
      present({ message: res.info, duration: 3500, color: res.estado === "CONFIRMADA" ? "success" : "primary" });
      history.replace("/agenda?tab=mis");
    } catch (err: any) {
      const data = err?.response?.data;
      if (data?.puede_esperar) {
        presentAlert({
          header: "Turno completo",
          message: "Se completó el cupo de ese turno. ¿Querés anotarte en la lista de espera? Si alguien cancela, te avisamos.",
          buttons: ["No", { text: "Anotarme", handler: () => enviar(elegido, true) }],
        });
      } else {
        present({ message: mensajeError(err, "No se pudo reservar"), duration: 4500, color: "danger" });
      }
      // El turno pudo haberse ocupado: se refresca la grilla.
      refrescarTramo(elegido.inicio.slice(0, 10));
      setTurno(null);
    } finally {
      setEnviando(false);
    }
  };

  if (cargando || !tema) {
    return (
      <IonPage>
        <Header titulo="Reservar" botonIzq={{ icono: arrowBack, url: "/agenda" }} />
        <IonContent>
          <div style={{ display: "flex", justifyContent: "center", padding: 32 }}>
            <CircularProgress />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  const color = colorCategoria(tema.categoria);
  const limiteAlcanzado = !reprogramarId && tema.mis_activas >= tema.max_reservas_activas;
  const bloqueado = Boolean(tema.penalizado_hasta) || !inscripcionAbierta || limiteAlcanzado;
  const textoBoton = reprogramarId
    ? "Confirmar nuevo horario"
    : turno && turno.disponibles === 0
    ? "Anotarme en lista de espera"
    : tema.requiere_aprobacion
    ? "Solicitar turno"
    : "Reservar";

  return (
    <IonPage>
      <Header titulo={reprogramarId ? "Reprogramar cita" : "Reservar"} botonIzq={{ icono: arrowBack, url: "/agenda" }} />
      <IonContent id="agenda-reservar">
        <div className="agenda-page campos-mui">
          {/* ---------------- Información de la agenda ---------------- */}
          <div className="agenda-card acento" style={{ ["--agenda-color" as any]: color }}>
            <span className="agenda-etiqueta">{CATEGORIAS[tema.categoria]?.etiqueta}</span>
            <p className="agenda-card-titulo" style={{ fontSize: 18 }}>{tema.titulo}</p>
            {tema.descripcion && <p className="agenda-card-sub" style={{ whiteSpace: "pre-line" }}>{tema.descripcion}</p>}
            <div className="agenda-meta">
              <span>
                <IonIcon icon={timeOutline} /> {tema.duracion_minutos} min
              </span>
              <span>{ETIQUETA_MODALIDAD[tema.modalidad]}</span>
              {tema.lugar && (
                <span>
                  <IonIcon icon={locationOutline} /> {tema.lugar}
                </span>
              )}
              {tema.tipo_agenda === "GRUPAL" && (
                <span>
                  <IonIcon icon={peopleOutline} /> Sesión grupal
                </span>
              )}
            </div>
            {tema.responsables.length > 0 && (
              <div className="agenda-meta">
                <span>
                  Atiende: {tema.responsables.map((r) => (r.detalle ? `${r.nombre} (${r.detalle})` : r.nombre)).join(", ")}
                </span>
              </div>
            )}
            <div className="agenda-meta" style={{ fontSize: 12 }}>
              {tema.requiere_aprobacion && <span>Requiere aprobación</span>}
              <span>Cancelás hasta {tema.cancelacion_min_horas} h antes</span>
              {tema.max_reservas_total && (
                <span>
                  Máx. {tema.max_reservas_total} cita{tema.max_reservas_total > 1 ? "s" : ""} por persona
                </span>
              )}
              {tema.penalizacion_dias && <span>Si faltás sin cancelar: {tema.penalizacion_dias} días sin reservar</span>}
            </div>
          </div>

          {tema.penalizado_hasta && (
            <div className="agenda-aviso danger">
              Por una inasistencia no podés reservar en esta agenda hasta el {formatFechaLarga(tema.penalizado_hasta)}.
            </div>
          )}
          {!inscripcionAbierta && (
            <div className="agenda-aviso warning">
              Las inscripciones abren el {formatFechaLarga(tema.apertura_inscripcion)}. Podés ver los horarios, pero todavía no reservar.
            </div>
          )}
          {limiteAlcanzado && (
            <div className="agenda-aviso warning">
              Ya tenés {tema.mis_activas} reserva(s) activa(s) en esta agenda. Podés cancelarla o reprogramarla desde "Mis citas".
            </div>
          )}
          {reprogramarId && (
            <div className="agenda-aviso info">Elegí el nuevo horario: tu cita actual se cancela al confirmar.</div>
          )}

          {/* ---------------- Selección del turno ---------------- */}
          <div className="agenda-card">
            <p className="agenda-card-titulo">Elegí día y horario</p>
            {tema.responsables.length > 1 && (
              <div className="agenda-chips">
                <button type="button" className={`agenda-chip${responsable === "TODOS" ? " activo" : ""}`}
                  onClick={() => { setResponsable("TODOS"); setTurno(null); }}>
                  Cualquiera
                </button>
                {tema.responsables.map((r) => (
                  <button key={r.id} type="button" className={`agenda-chip${responsable === r.id ? " activo" : ""}`}
                    onClick={() => { setResponsable(r.id); setTurno(null); }}>
                    {r.nombre}
                  </button>
                ))}
              </div>
            )}

            {dias.length === 0 ? (
              <div className="agenda-vacio">
                <IonIcon icon={calendarOutline} />
                {hayMas ? "No hay turnos en las próximas semanas." : "No hay turnos disponibles por ahora. Volvé a mirar más adelante."}
              </div>
            ) : (
              <>
                <div className="agenda-chips">
                  {dias.map(([d, lista], i) => {
                    const fecha = new Date(`${d}T00:00:00`);
                    const libres = lista.filter((t) => t.disponibles > 0 && !t.mi_estado).length;
                    // El mes se muestra en el primer día de cada mes de la lista.
                    const nuevoMes = i === 0 || dias[i - 1][0].slice(0, 7) !== d.slice(0, 7);
                    return (
                      <button key={d} type="button" className={`agenda-dia${dia === d ? " activo" : ""}`}
                        onClick={() => { setDia(d); setTurno(null); }}>
                        <div className="mes">{nuevoMes ? fecha.toLocaleDateString("es-PY", { month: "short" }) : " "}</div>
                        <div className="sem">{fecha.toLocaleDateString("es-PY", { weekday: "short" })}</div>
                        <div className="num">{fecha.getDate()}</div>
                        <div className="libres">{libres > 0 ? `${libres} libre${libres > 1 ? "s" : ""}` : "lleno"}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="agenda-turnos" style={{ marginTop: 8 }}>
                  {turnosDelDia.map((t) => {
                    const lleno = t.disponibles === 0;
                    const mio = Boolean(t.mi_estado);
                    const activo = turno?.inicio === t.inicio && turno?.responsable_id === t.responsable_id;
                    return (
                      <button key={`${t.responsable_id}-${t.inicio}`} type="button"
                        disabled={mio || bloqueado || (Boolean(reprogramarId) && lleno)}
                        className={`agenda-turno${lleno ? " lleno" : ""}${mio ? " mio" : ""}${activo ? " activo" : ""}`}
                        onClick={() => setTurno(t)}>
                        {hora(t.inicio)}
                        <small>
                          {mio
                            ? "Tu cita"
                            : lleno
                            ? "Lista de espera"
                            : tema.tipo_agenda === "GRUPAL"
                            ? `${t.disponibles} lugar${t.disponibles > 1 ? "es" : ""}`
                            : "Libre"}
                        </small>
                        {responsable === "TODOS" && tema.responsables.length > 1 && <small>{t.responsable_nombre}</small>}
                      </button>
                    );
                  })}
                </div>
              </>
            )}
          </div>

          {hayMas && (
            <IonButton expand="block" fill="outline" disabled={cargandoMas} onClick={verMasFechas}
              style={{ marginTop: -4, marginBottom: 16 }}>
              {cargandoMas ? "Cargando..." : "Ver más fechas"}
            </IonButton>
          )}

          {/* ---------------- Confirmación ---------------- */}
          {turno && !bloqueado && (
            <div className="agenda-card">
              <p className="agenda-card-titulo">{formatFechaLarga(turno.inicio)}</p>
              <p className="agenda-card-sub">
                Hasta las {hora(turno.fin)}
                {turno.responsable_nombre ? ` · con ${turno.responsable_nombre}` : ""}
              </p>
              {turno.disponibles === 0 && (
                <div className="agenda-aviso info">
                  Este turno está completo. Si te anotás en la lista de espera y se libera un lugar, lo tomás automáticamente y te avisamos.
                </div>
              )}

              {tema.preguntas.length > 0 && (
                <div className="agenda-form-col" style={{ marginBottom: 14 }}>
                  {reprogramarId && (
                    <div className="agenda-card-sub" style={{ margin: 0 }}>
                      Se conservan tus respuestas anteriores. Completá solo lo que quieras cambiar o lo que se agregó.
                    </div>
                  )}
                  {tema.confidencial && (
                    <div className="agenda-card-sub" style={{ margin: 0 }}>
                      Tus respuestas solo las ve quien te atiende.
                    </div>
                  )}
                  {tema.preguntas.map((p) =>
                    p.tipo === "OPCION" ? (
                      <div key={p.id}>
                        <span className="agenda-label">
                          {p.texto}
                          {p.obligatoria ? " *" : ""}
                        </span>
                        <select className="agenda-select" value={respuestas[p.id] || ""}
                          onChange={(e) => setRespuestas((prev) => ({ ...prev, [p.id]: e.target.value }))}>
                          <option value="">Elegí una opción</option>
                          {p.opciones.map((o) => (
                            <option key={o} value={o}>{o}</option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <TextField key={p.id} label={`${p.texto}${p.obligatoria ? " *" : ""}`} variant="outlined" fullWidth multiline
                        value={respuestas[p.id] || ""} inputProps={{ maxLength: 1000 }}
                        onChange={(e) => {
                          const valor = e.target.value;
                          setRespuestas((prev) => ({ ...prev, [p.id]: valor }));
                        }} />
                    )
                  )}
                </div>
              )}

              <IonButton expand="block" disabled={enviando} onClick={() => enviar(turno, turno.disponibles === 0)}>
                {enviando ? "Enviando..." : textoBoton}
              </IonButton>
              {tema.requiere_aprobacion && !reprogramarId && turno.disponibles > 0 && (
                <p className="agenda-card-sub" style={{ textAlign: "center", marginTop: 8 }}>
                  Te avisamos cuando la aprueben.
                </p>
              )}
            </div>
          )}

          {tema.vigencia_hasta && (
            <p className="agenda-card-sub" style={{ textAlign: "center" }}>
              Agenda vigente hasta el {formatFechaCorta(`${tema.vigencia_hasta} 00:00`).split(" · ")[0]}.
            </p>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default AgendaReservar;
